<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { helpers } from '@vuelidate/validators'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import { useUserStore, useCommonStore, useFeaturesStore } from '@/shared/store'
  import { useValidation, useValidators } from '@/shared/composables'
  import {
    PROCEED_EVENT_NAME,
    LOAD_EVENT_NAME,
    UAE_PHONE_CODE,
  } from './FlowPersonalInformationWidgetQC.constants'
  import {
    INBOUND_MAX_AGE,
    OUTBOUND_MAX_AGE,
    GENDER_MALE,
    GENDER_FEMALE,
    DEFAULT_START_DATE,
  } from '@/shared/constants/flow.constants'
  import FlowDataProtectReminderQC from '@/entities/Flow/components/FlowDataProtectReminderQC/FlowDataProtectReminderQC.vue'
  import { TravelDirection } from '@/shared/types/common.types'
  import { getUnixTimeStamp } from '@/shared/utils/UnixTime'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import type { Nationality } from '@/shared/types/api.types'
  import { pushEvent } from '@/shared/composables/usePushEvent'
  import { decreaseYears, increaseYears } from '@/shared/utils'
  import { formatAnalyticsDate } from '@/shared/services/GoogleAnalyticsService'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import InputQC from '@qicgroupdoha/qic-connect-ds/components/InputQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import InputDateQC from '@qicgroupdoha/qic-connect-ds/components/InputDateQC'
  import RadioQC from '@qicgroupdoha/qic-connect-ds/components/RadioQC'
  import SelectQC from '@qicgroupdoha/qic-connect-ds/components/SelectQC'
  import InputEmailQC from '@qicgroupdoha/qic-connect-ds/components/InputEmailQC'
  import PhoneInputQC from '@qicgroupdoha/qic-connect-ds/components/PhoneInputQC'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import ErrorsListQC from '@qicgroupdoha/qic-connect-ds/components/ErrorsListQC'

  import {
    ID_MASK_CONFIG,
    ID_MAX_LENGTH,
    PASSPORT_MASK_CONFIG,
    PASSPORT_MAX_LENGTH,
    DEFAULT_MAX_LENGTH,
    NAME_MASK_CONFIG,
  } from '@/shared/constants/masks.constants'

  const props = withDefaults(defineProps<Props>(), {
    isEdit: false,
    withWinterSport: true,
    typeOfTravel: TravelDirection.OUTBOUND,
    loadingFormData: false,
    withBackButton: false,
  })

  const emit = defineEmits<Emits>()

  interface Props {
    isEdit?: boolean
    withWinterSport?: boolean
    typeOfTravel?: Exclude<TravelDirection, TravelDirection.UNKNOWN>
    loadingFormData?: boolean,
    withBackButton?: boolean
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
    (e: 'back'): void
  }

  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { t, locale } = useI18n()
  const gaService = useGoogleAnalytics()
  const featuresStore = useFeaturesStore()
  const isGeorgiaWinterCoverEnabled = computed(() => featuresStore.isGeorgiaWinterCoverEnabled)
  const shouldSendWinterSport = computed(() => {
    if (props.typeOfTravel !== TravelDirection.OUTBOUND) return false
    return (
      props.withWinterSport || (userStore.isGeorgiaDestination && isGeorgiaWinterCoverEnabled.value)
    )
  })

  const firstName = ref(userStore.firstName ?? '')
  const familyName = ref(userStore.familyName ?? '')
  const dateOfBirth = ref(userStore.dateOfBirth)
  const nationality = ref(userStore.nationality)
  const nationalityCode = ref(nationality.value?.nationalityCode || '')
  const emiratesId = ref(userStore.emiratesId ?? '')
  const civilIdExpiryDate = ref(userStore.civilIdExpiryDate)
  const passportNumber = ref(userStore.passportNumber ?? '')
  const email = ref(userStore.email ?? '')
  const emailErrorMessage = ref('')
  const phone = ref(userStore.phone ?? '')
  const maskedPhone = ref(userStore.maskedPhone ?? '')
  const loading = ref(false)

  const initWinterSport = () => {
    if (isGeorgiaWinterCoverEnabled.value && userStore.isGeorgiaDestination) {
      return true
    }
    return userStore.withWinterSport
  }

  const isWinterSportChecked = ref(initWinterSport())

  const nowDate = new Date()
  const countryCode = ref(userStore.countryCode ?? UAE_PHONE_CODE)
  const gender = ref(userStore.gender ?? GENDER_MALE)

  const isOutbound = props.typeOfTravel === TravelDirection.OUTBOUND

  const translates = {
    header: t('travel.common.fiil_personal_info.fill.header'),
    firstName: isOutbound
      ? t('outbound.travelers.first_name')
      : t('inbound_travelers_add_first_name'),
    familyName: isOutbound
      ? t('outbound.travelers.last_name')
      : t('inbound_travelers_add_last_name'),
    birthdate: isOutbound ? t('outbound_dob_date') : t('inbound_dob_date'),
    nationality: isOutbound
      ? t('outbound_nationality_nationality')
      : t('inbound_nationality_nationality'),
    contacts: isOutbound ? t('outbound.contacts.title') : t('inbound.contacts.title'),
    email: isOutbound ? t('outbound.travelers.email') : t('inbound_travelers_add_email'),
  }

  const phoneCompleted = ref(false)

  const nationalities = computed(() => commonStore.nationalities)
  const maxAge = computed(() => (isOutbound ? OUTBOUND_MAX_AGE : INBOUND_MAX_AGE))

  const MIN_DATE_OF_BIRTH = computed(() => decreaseYears(nowDate, maxAge.value))
  const MAX_DATE_OF_BIRTH = computed(() => decreaseYears(nowDate, 18))

  const { emiratesIdValidator, emailValidator, phoneValidator, required } = useValidators()
  const emiratesIdRules = computed(() => (isOutbound ? { ...emiratesIdValidator } : {}))
  const civilIdExpiryDateRules = computed(() => (isOutbound ? { required } : {}))

  const validationRules = computed(() => {
    const commonRules = {
      firstName: { required },
      familyName: { required },
      dateOfBirth: { required },
      nationalityCode: { required },
      emiratesId: emiratesIdRules.value,
      civilIdExpiryDate: civilIdExpiryDateRules.value,
      passportNumber: { required },
      gender: { required: helpers.withMessage(t('travel.common.choose_option.error'), required) },
    }

    const additionalRules: any = {}
    if (props.isEdit) {
      additionalRules.email = {
        ...emailValidator,
        errorMessage: helpers.withMessage(
          t('qic.error.email.format'),
          () => !emailErrorMessage.value
        ),
      }

      additionalRules.phone = { required }
      additionalRules.phoneCompleted = { ...phoneValidator }
    }

    return { ...commonRules, ...additionalRules }
  })

  const { v$, errors } = useValidation(validationRules, {
    firstName,
    familyName,
    dateOfBirth,
    nationalityCode,
    emiratesId,
    civilIdExpiryDate,
    passportNumber,
    email,
    phone,
    phoneCompleted,
    gender,
  })

  const validateEmail = () =>
    commonStore.validateEmail(email.value).then((message) => {
      emailErrorMessage.value = message
      return message
    })

  const resetPhoneErrorMessage = () => {
    v$.value.phone?.$reset()
    v$.value.phoneCompleted?.$reset()
  }

  const nationalityName = computed(
    () =>
      nationalities.value.find((option) => option.nationalityCode === nationalityCode.value)
        ?.nationalityName
  )

  const onProceed = async () => {
    v$.value.$touch()

    if (!v$.value.$error) {
      await emit(LOAD_EVENT_NAME, async () => {
        loading.value = true
        let message = ''
        if (props.isEdit) {
          message = await validateEmail()
        }

        if (message) {
          loading.value = false
          return
        }

        userStore.setFirstName(firstName.value, true)
        userStore.setFamilyName(familyName.value, true)
        userStore.setDateOfBirth(dateOfBirth.value, true)
        userStore.setNationality(nationality.value, true)
        userStore.setPassportNumber(passportNumber.value, true)
        userStore.setMaskedPhone(maskedPhone.value, true)
        userStore.setGender(gender.value, true)

        if (props.isEdit) {
          userStore.setEmail(email.value, true)
          userStore.setPhone(phone.value, true)
        }

        if (isOutbound) {
          userStore.setEmiratesId(emiratesId.value, true)
          userStore.setCivilIdExpiryDate(civilIdExpiryDate.value, true)
          if (shouldSendWinterSport.value) {
            userStore.setWithWinterSport(isWinterSportChecked.value, true)
          }
        } else {
          userStore.setCountryCode(countryCode.value, true)
        }

        const winterSportData = shouldSendWinterSport.value
          ? { winSports: isWinterSportChecked.value ? 1 : 0 }
          : {}

        const data: any = {
          firstName: firstName.value.trim(),
          familyName: familyName.value.trim(),
          dateOfBirth: getUnixTimeStamp(new Date(dateOfBirth.value as Date)),
          nationalityCode: nationalityCode.value,
          nationalityName: nationalityName.value,
          qatarId: isOutbound ? emiratesId.value : '',
          passportNumber: passportNumber.value,
          gender: gender.value,
          ...winterSportData,
        }

        if (isOutbound) {
          data.civilIdExpiryDate = getUnixTimeStamp(new Date(civilIdExpiryDate.value as Date))
        }

        if (props.isEdit) {
          data.email = email.value
          data.phone = phone.value
        }

        await commonStore.sendFormData(data)
        const countries = userStore.destination?.map((country) => country?.id || '')

        if (nationality.value) {
          if (props.isEdit) {
            gaService.sendInsuredTravelersUpdateProceed(
              props.typeOfTravel,
              nationality.value.nationalityName
            )
          } else {
            gaService.sendPersonalInformationProceed(
              props.typeOfTravel,
              nationality.value.nationalityName,
              countries,
              dateOfBirth.value as Date,
              gender.value
            )
            pushEvent('form_submit', {
              product: 'travel',
              category: props.typeOfTravel,
              page: 'holder-info',
              first_name: firstName.value,
              family_name: familyName.value,
              date_of_birth: formatAnalyticsDate(dateOfBirth.value as Date),
              qid: isOutbound ? emiratesId.value : '',
              qid_expiry_date: isOutbound
                ? formatAnalyticsDate(civilIdExpiryDate.value as Date)
                : '',
              nationality: nationality.value.nationalityName,
              passport_number: passportNumber.value,
              email: email.value,
              phone_number: phone.value,
              gender: gender.value,
            })
          }
        }

        loading.value = false
        emit(PROCEED_EVENT_NAME)
      })
    }
  }

  const onFieldCompleted = (name: string, value: string | Date | null, position: number) => {
    if (!value || props.isEdit || !isOutbound) {
      return
    }

    gaService.sendPersonalInformationFieldCompleted(name, position)
  }

  const onPhoneChange = (data: { unmasked: string; value: string; code: string }) => {
    if (phoneCompleted.value) {
      phone.value = `${data.code}${data.unmasked}`
    }
  }

  watch(
    () => dateOfBirth.value,
    (current, old) => {
      if (!old && current) {
        onFieldCompleted('dob', dateOfBirth.value, 4)
      }
    }
  )

  watch(
    () => nationalityCode.value,
    () => {
      if (nationalityCode.value) {
        onFieldCompleted('nationality', nationalityCode.value, 5)
      }

      const newValue = nationalities.value.find(
        (option) => option.nationalityCode === nationalityCode.value
      )
      if (newValue) nationality.value = newValue
    }
  )

  const onEmiratesIdCompleted = (value: string | null | number) => {
    if (!value || typeof value !== 'string' || value.length !== 11) return

    let newBirthdate: Date | undefined
    let newNat: Nationality | undefined
    let newNatCode: string | undefined

    if (!dateOfBirth.value) {
      let year = ''
      if (value[0] === '2') {
        year = '19'
      } else {
        year = '20'
      }

      year = year + value[1] + value[2]
      newBirthdate = new Date(+year, 0, 1)
    }

    if (!nationalityCode.value) {
      const iso = value[3] + value[4] + value[5]
      newNatCode = nationalities.value.find((el) => el.isoCode === iso)?.nationalityCode
      newNat = nationalities.value.find((option) => option.nationalityCode === newNatCode)
    }

    if (
      newBirthdate &&
      (newBirthdate.getTime() > MAX_DATE_OF_BIRTH.value.getTime() ||
        newBirthdate.getTime() < MIN_DATE_OF_BIRTH.value.getTime())
    ) {
      newBirthdate = DEFAULT_START_DATE
    }

    if (newBirthdate && newNat && newNatCode) {
      gaService.sendQidFieldCompleted(newBirthdate, newNat.nationalityName)

      dateOfBirth.value = newBirthdate
      nationalityCode.value = newNatCode
    }
  }

  watch(
    () => props.loadingFormData,
    (newVal) => {
      if (!newVal) {
        firstName.value = userStore.firstName ?? ''
        familyName.value = userStore.familyName ?? ''
        dateOfBirth.value = userStore.dateOfBirth
        nationalityCode.value = userStore.nationality?.nationalityCode ?? ''
        if (isOutbound) {
          emiratesId.value = userStore.emiratesId ?? ''
          civilIdExpiryDate.value = userStore.civilIdExpiryDate
        }
        passportNumber.value = userStore.passportNumber ?? ''
        gender.value = userStore.gender ?? GENDER_MALE
        phone.value = userStore.phone ?? ''
        maskedPhone.value = userStore.maskedPhone ?? ''
        email.value = userStore.email ?? ''
      }
    }
  )
</script>

<template>
  <FlowCardQC
    :title="props.isEdit ? t('travellers_update_info') : translates.header"
    class="personal-information-qc"
    :with-button="false"
    :with-back-button="withBackButton"
    @back="emit('back')"
  >
    <WrapperQC
      v-if="!loadingFormData"
      direction="column"
      gap="var(--spacing-5)"
      class="personal-information-qc-inputs"
    >
      <InputQC
        v-model="firstName"
        :label="translates.firstName"
        :errors="errors.firstName"
        :mask-config="NAME_MASK_CONFIG"
        :max-length="DEFAULT_MAX_LENGTH"
        :container-attrs="{
          'data-test': 'policyholder-first-name-input',
        }"
        :is-filled="!v$.firstName.$invalid"
        data-test="policyholder-first-name-input-inner"
        @blur="onFieldCompleted('first_name', firstName, 1)"
      />
      <InputQC
        v-model="familyName"
        :label="translates.familyName"
        :errors="errors.familyName"
        :mask-config="NAME_MASK_CONFIG"
        :max-length="DEFAULT_MAX_LENGTH"
        :container-attrs="{
          'data-test': 'policyholder-last-name-input',
        }"
        :is-filled="!v$.familyName.$invalid"
        data-test="policyholder-last-name-input-inner"
        @blur="onFieldCompleted('family_name', familyName, 2)"
      />
      <!-- @vue-ignore -->
      <InputDateQC
        v-model="dateOfBirth"
        :label="translates.birthdate"
        :min-date="MIN_DATE_OF_BIRTH"
        :max-date="MAX_DATE_OF_BIRTH"
        :calendar-props="{
          startDate: dateOfBirth ?? DEFAULT_START_DATE,
        }"
        :locale="locale"
        allow-manual-input
        :errors="errors.dateOfBirth"
        :input-props="{
          containerAttrs: {
            'data-test': 'policyholder-birth-date-input',
          },
          isFilled: !v$.dateOfBirth.$invalid,
        }"
        data-test="policyholder-birth-date-input-inner"
      />

      <WrapperQC direction="column" gap="var(--spacing-3)">
        <WrapperQC gap="var(--spacing-3)">
          <!-- @vue-ignore -->
          <RadioQC
            v-model="gender"
            :value="GENDER_MALE"
            :error="errors.gender?.length > 0"
            variant="mini"
            data-test="gender-male-radio"
          >
            {{ $t('outbound.travelers.gender.male') }}
          </RadioQC>

          <!-- @vue-ignore -->
          <RadioQC
            v-model="gender"
            :value="GENDER_FEMALE"
            :error="errors.gender?.length > 0"
            variant="mini"
            data-test="gender-female-radio"
          >
            {{ $t('outbound.travelers.gender.female') }}
          </RadioQC>
        </WrapperQC>

        <ErrorsListQC v-if="errors.gender?.length > 0" :errors="errors.gender" />
      </WrapperQC>

      <InputQC
        v-if="isOutbound"
        v-model="emiratesId"
        input-mode="numeric"
        :label="$t('outbound.travelers.qatar_id')"
        :mask-config="ID_MASK_CONFIG"
        :max-length="ID_MAX_LENGTH"
        :errors="errors.emiratesId"
        :is-filled="!v$.emiratesId.$invalid"
        :container-attrs="{
          'data-test': 'policyholder-emirates-id-input',
        }"
        data-test="policyholder-emirates-id-input-inner"
        @input="onEmiratesIdCompleted"
      />

      <!-- @vue-ignore -->
      <InputDateQC
        v-if="isOutbound"
        v-model="civilIdExpiryDate"
        :label="$t('outbound.personal_info.qid_exp_date')"
        :errors="errors.civilIdExpiryDate"
        :locale="locale"
        :min-date="nowDate"
        :max-date="increaseYears(nowDate, 70)"
        :input-props="{
          containerAttrs: {
            'data-test': 'policyholder-qid-expiry-date-input',
          },
          isFilled: !v$.civilIdExpiryDate.$invalid,
        }"
        allow-manual-input
        data-test="policyholder-qid-expiry-date-input-inner"
      />

      <!-- @vue-ignore -->
      <SelectQC
        v-model="nationalityCode"
        :options="nationalities"
        :label="translates.nationality"
        title-key="nationalityName"
        value-key="nationalityCode"
        :errors="errors.nationalityCode"
        :locale="locale"
        :container-attrs="{
          'data-test': 'policyholder-nationality-input',
        }"
        data-test="policyholder-nationality-input-inner"
        is-filterable
      />

      <InputQC
        v-model="passportNumber"
        :label="$t('travellers_passport')"
        :errors="errors.passportNumber"
        :mask-config="PASSPORT_MASK_CONFIG"
        :max-length="PASSPORT_MAX_LENGTH"
        :is-filled="!v$.passportNumber.$invalid"
        :container-attrs="{
          'data-test': 'policyholder-passport-number-input',
        }"
        data-test="policyholder-passport-number-input-inner"
        @blur="onFieldCompleted('passport_number', passportNumber, 6)"
      />
    </WrapperQC>

    <WrapperQC v-if="isEdit" direction="column" gap="var(--spacing-5)">
      <WrapperQC gap="var(--spacing-3)">
        <IconQC name="phone" color="var(--colorIcnSrfPrimary)" />
        <h2 class="heading-6">
          {{ translates.contacts }}
        </h2>
      </WrapperQC>

      <InputEmailQC
        v-model="email"
        :label="translates.email"
        :errors="errors.email"
        :is-filled="!v$.email.$invalid"
        data-test="policyholder-email-input"
        @focus="v$.email.$reset()"
      />

      <PhoneInputQC
        v-model="maskedPhone"
        v-model:country-iso.uppercase="countryCode"
        :errors="[...errors.phone, ...errors.phoneCompleted]"
        data-test="policyholder-phone-input"
        @completed="phoneCompleted = $event"
        @focus="resetPhoneErrorMessage"
        @change="onPhoneChange"
      />
    </WrapperQC>

    <template #button>
      <ButtonQC
        wide
        :icon-after-content="props.isEdit ? 'none' : `arrow-narrow-${locale === 'en' ? 'right' : 'left'}`"
        :data-test="props.isEdit ? 'edit-save-button' : 'proceed-button'"
        :loading="loading"
        @click="onProceed"
      >
        {{
          props.isEdit
            ? $t('inbound_travelers_edit_save_button')
            : $t('travel.common.continue.button')
        }}
      </ButtonQC>
    </template>

    <template #after>
      <FlowDataProtectReminderQC />
    </template>
  </FlowCardQC>
</template>
